import { Building2, CalendarDays, ClipboardList, FilePlus2, FileText, KeyRound, LayoutDashboard, Link2, LogOut, Megaphone, Menu, Newspaper, Phone, Pill, Users, X } from 'lucide-react';
import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { cn } from './kit';
import { Logo } from './Logo';

export const NAV = [
  { to: '/', label: 'Tablero', icon: LayoutDashboard, end: true, permission: 'complaints.view' },
  { to: '/reclamos', label: 'Reclamos', icon: ClipboardList, end: false, permission: 'complaints.view' },
  { to: '/reclamos/nuevo', label: 'Cargar reclamo', icon: FilePlus2, end: true, permission: 'complaints.create_assisted' },
  { to: '/publicaciones', label: 'Noticias y comunicados', icon: Newspaper, end: false, permission: 'posts.create' },
  { to: '/alertas', label: 'Alertas', icon: Megaphone, end: false, permission: 'alerts.create' },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays, end: true, permission: 'events.manage' },
  { to: '/farmacias', label: 'Farmacias de turno', icon: Pill, end: true, permission: 'pharmacies.manage' },
  { to: '/telefonos', label: 'Teléfonos y oficinas', icon: Phone, end: true, permission: 'directory.manage' },
  { to: '/tramites', label: 'Trámites', icon: FileText, end: true, permission: 'procedures.manage' },
  { to: '/enlaces', label: 'Enlaces', icon: Link2, end: true, permission: 'links.manage' },
  { to: '/usuarios', label: 'Usuarios', icon: Users, end: false, permission: 'users.view' },
  { to: '/roles', label: 'Roles y permisos', icon: KeyRound, end: true, permission: 'users.view' },
  { to: '/areas', label: 'Áreas', icon: Building2, end: true, permission: 'departments.manage' },
];

export function Brand({ subtitle }: { subtitle: string }) {
  return (
    <div className="flex items-center gap-3">
      <Logo variant="inverse" height={40} />
      <div className="border-l border-white/25 pl-3 leading-tight">
        <div className="text-xs font-extrabold uppercase tracking-[0.2em] text-celeste">Más Cerca</div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-white/70">{subtitle}</div>
      </div>
    </div>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can, logout } = useAuth();
  return (
    <div className="flex h-full flex-col gap-6 bg-sidebar p-5 text-white">
      <Brand subtitle="Panel comunal" />
      <nav aria-label="Secciones" className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1">
        {NAV.filter((n) => can(n.permission)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
              isActive ? 'bg-white text-[#0B2A4A]' : 'text-white/85 hover:bg-white/10',
            )}>
            <n.icon className="size-5" aria-hidden />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-3 border-t border-white/15 pt-4">
        <div className="text-sm">
          <div className="font-bold">{[me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email}</div>
          <div className="text-white/70">{me?.departments.map((d) => d.name).join(', ') || me?.roles.join(', ')}</div>
        </div>
        <button type="button" onClick={() => void logout()} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white/85 hover:bg-white/10 cursor-pointer">
          <LogOut className="size-4" aria-hidden />Cerrar sesión
        </button>
      </div>
    </div>
  );
}

export function AdminLayout({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <div className="flex min-h-full">
      <a href="#contenido" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-lg focus:bg-surface focus:p-2">Saltar al contenido</a>
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block"><Sidebar /></aside>
      {open ? (
        <div className="fixed inset-0 z-40 flex lg:hidden" role="dialog" aria-modal="true" aria-label="Menú">
          <div className="w-72"><Sidebar onNavigate={() => setOpen(false)} /></div>
          <button type="button" aria-label="Cerrar menú" className="flex-1 bg-black/40" onClick={() => setOpen(false)}><X className="m-4 ml-auto size-6 text-white" /></button>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 bg-sidebar px-4 py-3 lg:hidden">
          <button type="button" aria-label="Abrir menú" onClick={() => setOpen(true)} className="rounded-lg p-2 text-white hover:bg-white/10 cursor-pointer"><Menu className="size-6" /></button>
          <Brand subtitle="Panel comunal" />
        </header>
        <main id="contenido" className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 lg:px-8"><Suspense fallback={null}>{children ?? <Outlet />}</Suspense></main>
      </div>
    </div>
  );
}
